import * as React from 'react';
import {Link as RouterLink} from 'react-router-dom';

import {EmptyStateLarge} from 'shells/empty-states/empty-state-large';
import {ErrorState} from 'shells/error-state/error-state';
import {Illustration} from 'shells/illustrations/illustration';
import {Link} from 'shells/link/link';
import {ListViewPaginatedDeprecated} from 'shells/list-view/list-view-paginated-deprecated';
import {LoadingPage} from 'shells/loading-page/loading-page';

import type {AudienceStub as Audience} from 'nutshell-graphql-types';

import {AudienceLastContactedTime} from '../../audiences/audience-last-contacted-time';
import {AudienceListQuery} from '../../graphql-wrappers/audience/audience-list-query';

import type {AudienceRecipientSelection} from './new-recipients-selection-view';
import {RecipientSelectorListItem} from './recipient-selector-list-item';

type OwnProps = {
    selectedRecipients: AudienceRecipientSelection | null | undefined;
    setSelectedRecipients: (arg1?: AudienceRecipientSelection | null | undefined) => void;
    canAccessAudiences: boolean;
    useSmsStats?: boolean;
};

type Props = OwnProps & {
    audiences: Audience[];
    error: string | null | undefined;
    isLoading: boolean;
    onFetchMoreRows: () => Promise<any>;
    hasNextPage: boolean;
};

function AudienceSelectorComponent(props: Props) {
    const selectAudience = (audience: Audience) => {
        const recipientCount = props.useSmsStats
            ? audience.smsMembershipStats.subscribedCount
            : audience.membershipStats.subscribedCount;

        props.setSelectedRecipients({
            id: audience.id,
            name: audience.name,
            htmlUrlPath: audience.htmlUrlPathForList,
            recipientCount,
            unsubscribedRate: audience.membershipStats.unsubscribedRate,
            membershipStats: audience.membershipStats,
            smsMembershipStats: audience.smsMembershipStats,
        });
    };

    React.useEffect(() => {
        if (!props.canAccessAudiences) {
            return;
        }

        if (!props.audiences.length && !props.isLoading && props.selectedRecipients) {
            props.setSelectedRecipients(null);
        }

        if (props.audiences.length > 0 && !props.selectedRecipients) {
            const firstAudience = props.audiences[0];
            if (!firstAudience) {
                return;
            }

            selectAudience(firstAudience);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.audiences, props.canAccessAudiences]);

    return (
        // @ts-expect-error
        <ListViewPaginatedDeprecated
            specialStates={[
                {
                    shouldRender: () => !props.canAccessAudiences,
                    component: (
                        <EmptyStateLarge
                            title='You don’t have permission to access any audiences'
                            subtitle={
                                <p>
                                    If you believe this to be a mistake, contact your Nutshell
                                    administrator
                                </p>
                            }
                            icon={
                                <div style={{width: 120, height: 120}}>
                                    <Illustration src='audience' width='100%' height='100%' />
                                </div>
                            }
                        />
                    ),
                    centerVertically: true,
                },
                {
                    shouldRender: () => props.isLoading,
                    component: <LoadingPage />,
                },
                {
                    shouldRender: () => Boolean(props.error),
                    component: <ErrorState errorMessage={props.error} />,
                },
                {
                    shouldRender: () => !props.isLoading && !props.audiences.length,
                    component: (
                        <EmptyStateLarge
                            title='You don’t have any audiences yet'
                            subtitle={
                                <span>
                                    <p>
                                        {' '}
                                        <Link
                                            variant='primary'
                                            size='big'
                                            // @ts-expect-error
                                            as={RouterLink}
                                            to='/email-marketing/audiences'
                                        >
                                            Create one
                                        </Link>{' '}
                                        and add some members to message them all at once
                                    </p>
                                </span>
                            }
                            icon={
                                <div style={{width: 120, height: 120}}>
                                    <Illustration src='audience' width='100%' height='100%' />
                                </div>
                            }
                        />
                    ),
                    centerVertically: true,
                },
            ]}
            isLoading={props.isLoading}
            renderRow={(audience) => {
                return (
                    <RecipientSelectorListItem
                        key={audience.id}
                        title={audience.name}
                        subtitle={getSubscribersElement(audience, props.useSmsStats)}
                        isSelected={Boolean(
                            props.selectedRecipients &&
                                props.selectedRecipients.name === audience.name
                        )}
                        onClick={() => selectAudience(audience)}
                        actionElement={
                            <div className='flex align-center'>
                                <AudienceLastContactedTime
                                    lastContactedTime={audience.lastContactedTime}
                                />
                            </div>
                        }
                    />
                );
            }}
            collection={props.audiences}
            onFetchMoreRows={props.onFetchMoreRows}
            hasNextPage={props.hasNextPage}
            itemSpacing={8}
            listPadding={16}
        />
    );
}

export function AudienceSelector(props: OwnProps) {
    return (
        <AudienceListQuery isArchived={false} sortOrder='NAME_ASC'>
            {({audiences, isLoading, error, fetchMoreRows, hasNextPage}) => (
                <AudienceSelectorComponent
                    {...props}
                    audiences={audiences}
                    error={error}
                    isLoading={isLoading}
                    onFetchMoreRows={fetchMoreRows}
                    hasNextPage={hasNextPage}
                />
            )}
        </AudienceListQuery>
    );
}

function getSubscribersElement(audience: Audience, useSmsStats?: boolean): string {
    const count = useSmsStats
        ? audience.smsMembershipStats.subscribedCount
        : audience.membershipStats.subscribedCount;
    const pluralSubscribers = count !== 1 ? 'subscribers' : 'subscriber';

    return `${count.toLocaleString()} ${pluralSubscribers}`;
}
